import React, { useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { ROUTES, navigate } from "../router";
import { t } from "../i18n";
import { useNavTimer } from "../gameTimer";
import { InitialsAvatar, PlayerName, frameClass, avatarLook, avatarSrc, bannerStyle } from "./PlayerIdentity";
import ThemeLogo from "./ThemeLogo";
import { confirmDialog } from "../dialog";
import BugReport, { BUG_REPORT_EVENT } from "./BugReport";
import { useFeatures } from "../features";

// Minuteur central : anneau qui se vide, chiffre au milieu. Il ne s'affiche que
// pendant une manche — le reste du temps la barre reste nue.
function NavTimer({ timer }) {
    const R = 19, C = 2 * Math.PI * R;
    const frac = timer.duration ? Math.max(0, Math.min(1, timer.remaining / (timer.duration / 1000))) : 1;
    return (
        <div class={`nav-timer${!timer.idle && timer.remaining <= 5 ? " urgent" : ""}`} role="timer" aria-label={t("game.timeLeft")}
          style={{ "--nav-frac": frac }}>
            <svg viewBox="0 0 44 44" aria-hidden="true">
                <g transform="translate(22 22) rotate(-90)">
                    <circle class="nav-timer-track" r={R} />
                    <circle class="nav-timer-arc" r={R}
                      style={{ strokeDasharray: C, strokeDashoffset: C * (1 - frac) }} />
                </g>
            </svg>
            <span class="nav-timer-num">{timer.idle ? "•" : timer.remaining}</span>
            {/* Les pièces d'une pendule à aiguille (Piscine) : masquées par
                défaut, un thème les montre s'il remplace l'anneau. */}
            <span class="nav-timer-mark nav-timer-mark-top" aria-hidden="true">60</span>
            <span class="nav-timer-mark nav-timer-mark-bottom" aria-hidden="true">30</span>
            <span class="nav-timer-hand" aria-hidden="true" style={{ "--nav-hand": `${(1 - frac) * 360}deg` }} />
            <span class="nav-timer-hub" aria-hidden="true" />
        </div>
    );
}

function Navigation({ player, account, hasOngoingGame, inGame, isHost, isAdmin, isSuperAdmin, onProfileClick, onSettingsClick, onShopClick, onAdminClick, onSystemClick, onLogout, onLeaveGame, onCloseLobby, menuButton }) {
    const [menuOpen, setMenuOpen] = useState(false);
    const features = useFeatures();
    const timer = useNavTimer();
    const [pos, setPos] = useState({ top: 0, right: 0 });
    const btnRef = useRef(null);
    const menuRef = useRef(null);


    // Close the menu on outside click (button and portalled menu excluded)
    useEffect(() => {
        if (!menuOpen) return;
        const onDocClick = (e) => {
            if (btnRef.current && btnRef.current.contains(e.target)) return;
            if (menuRef.current && menuRef.current.contains(e.target)) return;
            setMenuOpen(false);
        };
        document.addEventListener("mousedown", onDocClick);
        return () => document.removeEventListener("mousedown", onDocClick);
    }, [menuOpen]);

    // The menu is rendered in a portal: the navigation bar carries a mask-image
    // (wavy edge) that would clip a child positioned below it.
    const toggleMenu = () => {
        if (!menuOpen && btnRef.current) {
            const r = btnRef.current.getBoundingClientRect();
            setPos({ top: r.bottom + 8, right: Math.max(8, window.innerWidth - r.right) });
        }
        setMenuOpen(o => !o);
    };

    const runItem = (fn) => { setMenuOpen(false); if (fn) fn(); };
    // Se déconnecter efface l'identité et renvoie à l'entrée : on demande
    // confirmation, l'erreur de clic coûterait cher.
    const askLogout = async () => {
        if (await confirmDialog(t("nav.confirmLogout"))) runItem(onLogout);
        else setMenuOpen(false);
    };

    return (
        <>
        <div class="navigation">
            {/* L'administration y met son bouton de menu à la place du logo. */}
            {menuButton ? <div class="nav-menu-slot">{menuButton}</div> : (
            <a href={ROUTES.home} class="app-logo" aria-label={t("nav.home")}
              onClick={(e) => { e.preventDefault(); navigate(ROUTES.home); }}>
                <ThemeLogo />
            </a>
            )}
            {timer && <NavTimer timer={timer} />}
            <div class="right-section">
                {player && player.pseudo && (
                <div class="auth-wrapper">
                    {/* La bannière portée habille le bouton comme elle habille
                        le jeton en partie : c'est la même parure, elle doit se
                        voir au même endroit. */}
                    <button ref={btnRef} onClick={toggleMenu}
                      class={`btn Login-button${player.cosmetics?.banner ? " has-banner" : ""}`}
                      style={bannerStyle(player)}
                      aria-haspopup="true" aria-expanded={menuOpen} title={t("nav.profileMenu")}>
                      {/* L'avatar porté remplace la photo envoyée, quelle qu'elle soit. */}
                      {avatarSrc(player)
                        ? <img src={avatarSrc(player)} alt="" class={frameClass(player).trim()} />
                        : <InitialsAvatar name={player.pseudo} size={45} className={`nav-initials${frameClass(player)}`} look={avatarLook(player)} />}
                      <PlayerName player={player} />
                    </button>
                </div>
                )}
            </div>
            {menuOpen && ReactDOM.createPortal(
                <div class="profile-menu" role="menu" ref={menuRef}
                  style={{ top: `${pos.top}px`, right: `${pos.right}px` }}>
                    {/* En pleine manche, le menu se réduit à ce qui sert
                        pendant la partie : profil et boutique attendront. */}
                    {!inGame && (
                      <button class="profile-menu-item" role="menuitem" onClick={() => runItem(onProfileClick)}>
                        {t("nav.myProfile")}
                      </button>
                    )}
                    {/* Seule la boutique se signale par sa couleur : c'est la
                        seule destination qui coûte quelque chose. */}
                    {!inGame && account && onShopClick && (
                      <button class="profile-menu-item accent" role="menuitem" onClick={() => runItem(onShopClick)}>
                        {t("profile.shop")}
                      </button>
                    )}
                    <button class="profile-menu-item" role="menuitem" onClick={() => runItem(onSettingsClick)}>
                      {t("settings.title")}
                    </button>
                    {features.bugReport && (
                      <button class="profile-menu-item" role="menuitem"
                        onClick={() => runItem(() => window.dispatchEvent(new Event(BUG_REPORT_EVENT)))}>
                        {t("bugReport.button")}
                      </button>
                    )}
                    {/* La questiothèque tient les questions, l'administration le
                        serveur : deux portes, deux publics. Ni l'une ni l'autre
                        pendant une manche — on quitterait la partie en cours. */}
                    {!inGame && isAdmin && onAdminClick && (
                      <button class="profile-menu-item" role="menuitem" onClick={() => runItem(onAdminClick)}>
                        {t("nav.library")}
                      </button>
                    )}
                    {!inGame && isSuperAdmin && onSystemClick && (
                      <button class="profile-menu-item" role="menuitem" onClick={() => runItem(onSystemClick)}>
                        {t("nav.administration")}
                      </button>
                    )}
                    {/* Au salon, quitter et fermer sont déjà en bas de l'écran :
                        on ne les répète ici qu'une fois la partie lancée. */}
                    {inGame ? (
                      <>
                        <button class="profile-menu-item danger" role="menuitem" onClick={() => runItem(onLeaveGame)}>
                          {t("nav.leaveGame")}
                        </button>
                        {isHost && onCloseLobby && (
                          <button class="profile-menu-item danger strong" role="menuitem" onClick={() => runItem(onCloseLobby)}>
                            {t("profile.closeLobby")}
                          </button>
                        )}
                      </>
                    ) : !hasOngoingGame && account ? (
                      <button class="profile-menu-item danger" role="menuitem" onClick={askLogout}>
                        {t("nav.logout")}
                      </button>
                    ) : null}
                </div>,
                document.body
            )}
        </div>
        {/* La fenêtre « signaler un bug » suit l'en-tête : le menu du profil
            l'ouvre de partout. */}
        <BugReport />
        </>
    )
}

export default Navigation;
